// src/services/voice.js
//
// 🟢 VOICE MEMOS (web) — recorder + sender, built to the phone's contract.
//
// 🟥 THE FORMAT IS NOT NEGOTIABLE. The phone records m4a/AAC, and on receive
//    `permanentlyDownloadMedia(…, 'audio', …)` writes the file as `audio_<id>.m4a`
//    while `CryptoService.decryptAudio` forces the `.m4a` extension on BOTH paths —
//    it deliberately overrides the native magic-number sniff that decryptImage uses.
//    So the bytes are never inspected: whatever we upload gets called AAC. Android's
//    ExoPlayer often content-sniffs and survives, but iOS AVFoundation types the asset
//    from the extension, so a WebM/Opus (or WAV) payload loads as a broken asset and
//    playback throws. A browser's DEFAULT MediaRecorder output is exactly that.
//    Hence: we record audio/mp4 or we refuse, loudly, rather than send a memo that
//    looks fine here and is silent on the recipient's phone.
//
// The rest of the contract (ChatModal ~17683):
//    duration : integer SECONDS
//    waveform : array of 35 floats that are PIXEL BAR HEIGHTS clamped to [3, 25]
//               — an array in Firestore, JSON.stringify'd in the socket payload
//    bytes    : nacl.secretbox, key sealed per-device under `audioPerDeviceKeys`

import {
  collection, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { uploadEncryptedBytes, writeChatDoc, newTempId } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

export const WAVE_BARS = 35;   // the phone's targetBars
export const WAVE_MIN = 3;     // the phone's clamp floor
export const WAVE_MAX = 25;    // the phone's clamp ceiling
export const MAX_SECONDS = 300;

/** The only containers the phone can actually play. Ordered best-first. */
const M4A_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
];

export function pickRecordingMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const t of M4A_TYPES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* keep looking */ }
  }
  return null;
}

export function canRecordVoice() {
  return !!(navigator.mediaDevices?.getUserMedia) && !!pickRecordingMime();
}

/** Level → bar height, shaped like the phone's `2 + t^2.5 * 30` so a web memo's bars
 *  read at the same visual weight as one recorded on the app. */
function levelToHeight(level) {
  const t = Math.max(0, Math.min(1, level));
  const h = WAVE_MIN + (t ** 0.62) * (WAVE_MAX - WAVE_MIN);
  return Math.max(WAVE_MIN, Math.min(WAVE_MAX, h));
}

/** Squash an arbitrary run of live levels into the phone's 35 bars. */
export function buildWaveform(levels, bars = WAVE_BARS) {
  const out = [];
  if (!levels || !levels.length) return new Array(bars).fill(WAVE_MIN + 2);
  const per = levels.length / bars;
  for (let i = 0; i < bars; i++) {
    const a = Math.floor(i * per);
    const b = Math.max(a + 1, Math.floor((i + 1) * per));
    let sum = 0;
    let n = 0;
    for (let j = a; j < b && j < levels.length; j++) { sum += levels[j]; n++; }
    out.push(levelToHeight(n ? sum / n : 0));
  }
  // the phone plants a small shape when a memo is dead flat, so a silent recording
  // doesn't render as an invisible line
  if (out.every((v) => v <= WAVE_MIN + 0.5)) {
    [5, 12, 18, 12, 5].forEach((v, i) => { out[i] = v; });
  }
  return out;
}

/**
 * Start recording. Returns a handle:
 *   { stop(): Promise<{blob, bytes, durationSec, waveform, levels}>, cancel(), onLevel }
 * `onLevel(level0to1, elapsedMs)` fires ~20×/s for the live bars.
 */
export async function startRecording({ onLevel, onTick } = {}) {
  const mime = pickRecordingMime();
  if (!mime) {
    throw new Error(
      "This browser can't record in a format Allo's phone apps can play. Try Safari, or Chrome/Edge 130+."
    );
  }
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (e) {
    throw new Error(
      e?.name === 'NotAllowedError'
        ? 'Microphone permission was denied. Allow it in the address bar and try again.'
        : 'No microphone is available.'
    );
  }

  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.55;
  source.connect(analyser);
  const buf = new Uint8Array(analyser.fftSize);

  const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 96000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

  const levels = [];
  const startedAt = Date.now();
  let raf = 0;
  let lastSample = 0;

  const loop = () => {
    raf = requestAnimationFrame(loop);
    const now = Date.now();
    if (now - lastSample < 50) return;   // ~20 samples/second
    lastSample = now;
    analyser.getByteTimeDomainData(buf);
    // RMS around the 128 midpoint → 0..1
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sum += d * d; }
    const rms = Math.sqrt(sum / buf.length);
    const level = Math.min(1, rms * 3.2);  // voice rarely exceeds ~0.3 RMS
    levels.push(level);
    onLevel?.(level, now - startedAt);
    onTick?.(Math.floor((now - startedAt) / 1000));
  };

  const teardown = () => {
    cancelAnimationFrame(raf);
    try { stream.getTracks().forEach((t) => t.stop()); } catch {}
    try { source.disconnect(); } catch {}
    try { ctx.close(); } catch {}
  };

  rec.start(250);
  loop();

  return {
    mime,
    get elapsedMs() { return Date.now() - startedAt; },
    cancel() {
      try { if (rec.state !== 'inactive') rec.stop(); } catch {}
      teardown();
    },
    stop() {
      return new Promise((resolve, reject) => {
        const durationSec = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
        rec.onstop = async () => {
          teardown();
          try {
            const blob = new Blob(chunks, { type: mime });
            const bytes = new Uint8Array(await blob.arrayBuffer());
            resolve({ blob, bytes, durationSec, waveform: buildWaveform(levels), levels, mime });
          } catch (e) { reject(e); }
        };
        rec.onerror = (e) => { teardown(); reject(e?.error || new Error('Recording failed.')); };
        try { rec.stop(); } catch (e) { teardown(); reject(e); }
      });
    },
  };
}

/** Peaks straight from decoded audio — used to draw a real waveform for memos that
 *  arrive WITHOUT one (the phone's history export drops `waveform`, and a synced
 *  memo would otherwise render the app's hardcoded placeholder wave). */
export async function peaksFromAudio(url, bars = WAVE_BARS) {
  try {
    const res = await fetch(url);
    const arr = await res.arrayBuffer();
    const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 1, 22050);
    const audio = await ctx.decodeAudioData(arr);
    const data = audio.getChannelData(0);
    const per = Math.floor(data.length / bars) || 1;
    const out = [];
    for (let i = 0; i < bars; i++) {
      let peak = 0;
      const start = i * per;
      for (let j = start; j < start + per && j < data.length; j += 4) {
        const v = Math.abs(data[j]);
        if (v > peak) peak = v;
      }
      out.push(levelToHeight(Math.min(1, peak * 1.6)));
    }
    return { waveform: out, durationSec: Math.max(1, Math.round(audio.duration)) };
  } catch {
    return null;
  }
}

// ── send ──────────────────────────────────────────────────────────────────
export async function sendVoiceMessage({
  chat, myUid, myName, bytes, durationSec, waveform, onProgress, reply,
}) {
  if (!chat?.id || !bytes?.length) throw new Error('nothing to send');
  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = newTempId();

  const { url, k, n } = await uploadEncryptedBytes(bytes, onProgress);

  const sealed = await sealForUsers(JSON.stringify({ k, n }), participants);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure the recording — it was not sent.");
  }

  // exactly the phone's shape: seconds (int) and a REAL array of pixel heights
  const duration = Math.max(1, Math.round(durationSec));
  const wave = (waveform && waveform.length ? waveform : buildWaveform(null))
    .map((v) => Math.max(WAVE_MIN, Math.min(WAVE_MAX, Number(v) || WAVE_MIN)));

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;

  await setMessageDoc(msgRef, {
    type: 'audio',
    clientTempId,
    audioUrl: url,
    audioPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
    senderDeviceId: sealed.senderDeviceId,
    duration,
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    waveform: wave,            // ARRAY here…
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    // the sealed quote — an object in Firestore, an object on the wire (the server
    // re-stringifies replyTo itself); the two *PerDeviceKeys maps must stay objects
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: '🎤 Voice message', clientMsgId: clientTempId,
    extra: {
      type: 'audio',
      audioUrl: url,
      duration,
      waveform: JSON.stringify(wave),   // …STRING over the socket, like the phone
      audioPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
      senderDeviceId: sealed.senderDeviceId,
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview: '🎤 Voice message' });
  return messageId;
}

export function timeLabel(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
